import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { LockIcon, ArrowRight01Icon, ViewIcon, ViewOffSlashIcon } from '@hugeicons/core-free-icons';
import { api } from '../utils/api';
import { FLAGS } from '../config/flags';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [otp, setOtp] = useState(searchParams.get('otp') || searchParams.get('code') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const email = searchParams.get('email') || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!otp) {
      setError('Verification code is required');
      return;
    }
    if (!password) {
      setError('The password field is required.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    if (FLAGS.USE_MOCK_API) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
      setLoading(false);
      return;
    }

    try {
      await api.post('/reset-password', {
        email,
        otp,
        password,
        password_confirmation: confirmPassword
      });
      
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full space-y-8 bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-8 rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-float-lg">
        <div className="text-center">
          <h2 className="text-3xl font-black text-charcoal dark:text-white tracking-tight">Reset Your Password</h2>
          <p className="mt-2 text-muted-gray">Enter your code and new password</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-charcoal dark:text-gray-200 mb-1">
                Verification Code
              </label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                className="block w-full px-4 py-3 border border-light-gray/20 dark:border-gray-700/50 rounded-2xl focus:ring-soft-green focus:border-soft-green text-charcoal dark:text-white bg-off-white/50 dark:bg-gray-800/50"
                placeholder="Enter 6-digit code"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-charcoal dark:text-gray-200 mb-1">
                New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-gray">
                  <HugeiconsIcon icon={LockIcon} size={20} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  className="block w-full pl-10 pr-10 py-3 border border-light-gray/20 dark:border-gray-700/50 rounded-2xl focus:ring-soft-green focus:border-soft-green text-charcoal dark:text-white bg-off-white/50 dark:bg-gray-800/50"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-gray hover:text-charcoal dark:hover:text-white"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <HugeiconsIcon icon={showPassword ? ViewOffSlashIcon : ViewIcon} size={20} />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-charcoal dark:text-gray-200 mb-1">
                Confirm New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-gray">
                  <HugeiconsIcon icon={LockIcon} size={20} />
                </div>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={8}
                  className="block w-full pl-10 pr-10 py-3 border border-light-gray/20 dark:border-gray-700/50 rounded-2xl focus:ring-soft-green focus:border-soft-green text-charcoal dark:text-white bg-off-white/50 dark:bg-gray-800/50"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-gray hover:text-charcoal dark:hover:text-white"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <HugeiconsIcon icon={showConfirmPassword ? ViewOffSlashIcon : ViewIcon} size={20} />
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-100 dark:bg-red-900/20 border border-red-200/50 text-red-700 dark:text-red-300 px-4 py-3 rounded-2xl text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-100 dark:bg-green-900/20 border border-green-200/50 text-green-700 dark:text-green-300 px-4 py-3 rounded-2xl text-sm">
              Password has been reset successfully. Redirecting to login...
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="group relative w-full flex justify-center py-4 px-4 border border-transparent text-sm font-bold rounded-3xl text-white bg-soft-green hover:bg-light-mint focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-soft-green transition-all duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-float disabled:opacity-50"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
            <span className="absolute right-4 flex items-center">
              <HugeiconsIcon icon={ArrowRight01Icon} size={18} />
            </span>
          </button>
        </form>

        <div className="text-center">
          <p className="text-sm text-muted-gray">
            Know your password?{' '}
            <span 
              className="font-bold text-soft-green hover:text-light-mint cursor-pointer"
              onClick={() => navigate('/login')}
            >
              Sign In
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;